import { type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '../../lib/api/api-context.js';
import { getReadUrl } from '../../lib/media/media-api.js';
import type { Media } from '../../lib/media/media-contract.js';
import { Button, Dialog, DialogContent, DialogTitle, Skeleton } from '@tailoring-crm/ui';

/**
 * The only place a full-size image is ever downloaded.
 *
 * Everywhere else — every list, every card, the gallery grid — renders the ~15KB thumbnail. The
 * 300KB original is fetched here, on demand, for the one image the person actually opened. On a
 * metered 4G connection that is the difference between a gallery costing 60KB and 1.8MB.
 *
 * The URL itself is fetched per view rather than stored: read URLs are signed and live about two
 * minutes, so there is nothing durable to cache and nothing that keeps working if it leaks.
 */

export interface MediaLightboxProps {
  items: Media[];
  /** Id of the image being shown, or null when the lightbox is closed. */
  activeId: string | null;
  onActiveIdChange: (id: string | null) => void;
}

export function MediaLightbox({
  items,
  activeId,
  onActiveIdChange,
}: MediaLightboxProps): ReactElement | null {
  const { t } = useTranslation();
  const client = useApiClient();

  const index = items.findIndex((item) => item.id === activeId);
  const active = index >= 0 ? items[index] : undefined;

  const readUrl = useQuery({
    queryKey: ['media', activeId, 'url', 'full'],
    queryFn: async () => getReadUrl(client, activeId ?? '', 'full'),
    enabled: activeId !== null,
    // Signed and short-lived: there is no point holding it once the lightbox closes.
    gcTime: 0,
    staleTime: 60_000,
    retry: 1,
  });

  if (!active) return null;

  const go = (delta: number): void => {
    const next = items[index + delta];
    if (next) onActiveIdChange(next.id);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onActiveIdChange(null)}>
      <DialogContent className="sm:max-w-3xl">
        <DialogTitle className="pr-8 text-base">
          {active.caption ?? active.originalFilename}
        </DialogTitle>

        <div className="relative flex min-h-[50vh] items-center justify-center rounded-md bg-muted">
          {readUrl.isPending ? (
            <Skeleton className="h-[50vh] w-full" />
          ) : readUrl.isError || !readUrl.data ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              {t('media.lightbox.failed')}
            </p>
          ) : (
            <img
              src={readUrl.data.url}
              alt={active.caption ?? active.originalFilename}
              className="max-h-[70vh] w-auto max-w-full rounded-md object-contain"
            />
          )}
        </div>

        {items.length > 1 && (
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="icon"
              onClick={() => go(-1)}
              disabled={index === 0}
              aria-label={t('media.lightbox.previous')}
            >
              <ChevronLeft aria-hidden="true" />
            </Button>
            <span className="text-sm text-muted-foreground">
              {t('media.lightbox.position', { position: index + 1, total: items.length })}
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={() => go(1)}
              disabled={index === items.length - 1}
              aria-label={t('media.lightbox.next')}
            >
              <ChevronRight aria-hidden="true" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
